import { ReactNode } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";
import { formatRelativeUpdated } from "./format";

interface SectionShellProps {
  title: ReactNode;
  /** Right-aligned subtle text (e.g. "vs last 7 days", "AI-detected · updated 4 min ago"). */
  rightLabel?: ReactNode;
  /** Inline content rendered between title and right-label (e.g. severity counts). */
  titleAdornment?: ReactNode;
  /** Right-side toolbar widgets (e.g. platform tabs). Rendered before the refresh button. */
  toolbar?: ReactNode;
  /** ISO-8601 from the section's envelope (or the combined `synced_at`). */
  syncedAt?: string | null;
  /** True while the per-section "refresh" mutation is in flight. */
  refreshing?: boolean;
  onRefresh?: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * Flat (no-Card) section wrapper that mirrors the mock: a single header row
 * with the section title on the left and an optional subtle label on the
 * right ("vs last 7 days", "AI-detected · updated 4 min ago"). The refresh
 * button is intentionally tucked next to the right label as a small icon
 * button — it's a power-user affordance, not a primary action.
 */
export function SectionShell({
  title,
  rightLabel,
  titleAdornment,
  toolbar,
  syncedAt,
  refreshing,
  onRefresh,
  children,
  className,
}: SectionShellProps) {
  return (
    <section className={cn("space-y-3", className)}>
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
          {titleAdornment}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {toolbar}
          {rightLabel && <span className="text-xs text-muted-foreground hidden sm:inline">{rightLabel}</span>}
          {!rightLabel && syncedAt && (
            <span className="text-xs text-muted-foreground/80 hidden sm:inline">
              Updated {formatRelativeUpdated(syncedAt)}
            </span>
          )}
          {onRefresh && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={onRefresh}
              disabled={refreshing}
              title="Refetch this section, bypassing the 24h cache"
            >
              {refreshing ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="size-3.5" />
              )}
            </Button>
          )}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

/**
 * Compact "this section failed to load" placeholder for use when the combined
 * endpoint returned `null` for a section (with a matching entry in `errors`).
 * Per the contract: render each section independently — never blow up the
 * whole page on a single section's failure.
 */
export function SectionErrorState({
  message,
  onRetry,
  retrying,
}: {
  message?: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
      <span>{message || "Couldn't load this section."}</span>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying} className="h-7 px-2 text-xs">
          {retrying ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
          <span className="ml-1.5">Retry</span>
        </Button>
      )}
    </div>
  );
}

export function SectionSkeletonRows({ count = 3, className }: { count?: number; className?: string }) {
  return (
    <div className={cn("space-y-2.5", className)}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
